/**
 * Machines as /process-config's Machine Setup offers them: Machine Category → Group → Machines.
 * Used by the step's Equipment (Where it perform), an ingredient's Source / Destination
 * Equipment, and a monitored parameter's Machine.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Cascader, Typography } from "antd";
import Axios from "axios";

const { Text } = Typography;

/** The equipment this step is worked on — the machines standing in the room chosen under
 *  "May run in", from the plant's area master. The area comes first: until one is chosen
 *  there is no room to list equipment from, so the picker waits and says so. The record says
 *  which; the floor uses them. Staged like the step's other fields. */
// 2026-10-07 · the machines as /process-config's Machine Setup offers them (mbmr/MachineSetup.js):
// Machine Category → Machine Group → Machines, a whole group at once or single machines, from
// the same master (get_machine_category_with_groups). Fetched once per page.
let machineTree = null;
let machineTreePlant = null;
/** The plant selected on the header, as every other call sends it (utils/api.js). Without it the server
 *  answers for the employee's own plant, and a machine of the selected plant is missing from the list. */
const selectedPlantId = () => {
  try {
    return JSON.parse(localStorage.getItem("active-plant") || "null")?.id || null;
  } catch {
    return null;
  }
};
export function useMachineTree() {
  const plantId = selectedPlantId();
  const [tree, setTree] = useState(machineTreePlant === plantId ? machineTree : null);
  useEffect(() => {
    if (machineTree && machineTreePlant === plantId) return undefined;
    let alive = true;
    Axios.get("/bp/get_machine_category_with_groups", {
      params: plantId ? { plant_id: plantId } : {},
      headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
    })
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        machineTree = data.map((c) => {
          const groups = (c.machine_groups || []).filter((g) => (g.machines || []).length);
          const count = groups.reduce((n, g) => n + g.machines.length, 0);
          return {
            value: `c${c.machine_category_id}`, label: `${c.machine_category_name} (${count})`,
            children: groups.map((g) => ({
              value: `g${g.machine_group_id}`, label: `${g.machine_group_name} (${g.machines.length})`,
              children: g.machines.map((m) => ({ value: m.machine_id, label: m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name })),
            })),
          };
        }).filter((c) => c.children.length);
        machineTreePlant = plantId;
        if (alive) setTree(machineTree);
      })
      .catch(() => { if (alive) setTree([]); });
    return () => { alive = false; };
  }, [plantId]);
  return tree;
}

export function EquipmentPicker({ step, areaList = [], readOnly, onPick }) {
  const fullTree = useMachineTree();
  // The area is chosen first ("May run in"); the machines offered are the ones standing in it,
  // laid out as the machine master groups them.
  const areaIds = (step.area_ids ?? (step.areas || []).map((a) => a.id)).map(String);
  const inArea = useMemo(() => {
    const ids = new Set();
    (areaList || []).filter((a) => areaIds.includes(String(a.id)))
      .forEach((a) => (a.booths || []).forEach((m) => ids.add(Number(m.id))));
    return ids;
  }, [areaList, areaIds.join(",")]);   // eslint-disable-line react-hooks/exhaustive-deps
  const tree = useMemo(() => (fullTree ? fullTree.map((c) => ({
    ...c,
    children: c.children.map((g) => ({ ...g, children: g.children.filter((m) => inArea.has(Number(m.value))) }))
      .filter((g) => g.children.length)
      .map((g) => ({ ...g, label: g.label.replace(/\(\d+\)$/, `(${g.children.length})`) })),
  })).filter((c) => c.children.length)
    .map((c) => ({ ...c, label: c.label.replace(/\(\d+\)$/, `(${c.children.reduce((n, g) => n + g.children.length, 0)})`) }))
    : null), [fullTree, inArea]);
  const chosen = (step.booth_ids ?? (step.booths || []).map((b) => b.id)).map(Number);
  // A saved machine id → its path in the tree, so the cascader shows it checked.
  const pathOf = useMemo(() => {
    const out = {};
    (tree || []).forEach((c) => c.children.forEach((g) => g.children.forEach((m) => { out[m.value] = [c.value, g.value, m.value]; })));
    return out;
  }, [tree]);
  const value = chosen.map((id) => pathOf[id]).filter(Boolean);
  const unknown = (step.booths || []).filter((b) => !pathOf[b.id]);
  return (
    <>
      <Cascader
        size="small" multiple showCheckedStrategy={Cascader.SHOW_CHILD}
        style={{ width: "100%" }} disabled={readOnly || !tree || (!areaIds.length && !chosen.length)} loading={!tree}
        options={tree || []} value={value} expandTrigger="hover"
        notFoundContent="No machine of the machine master stands in this area."
        showSearch={{ filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }}
        placeholder={areaIds.length ? "Machine category → group → machines" : "Choose \"May run in\" first"}
        onChange={(paths) => {
          const ids = (paths || []).map((p) => p[p.length - 1]).filter((v) => typeof v === "number");
          onPick([...ids, ...unknown.map((b) => b.id)]);
        }}
      />
      {unknown.length ? (
        <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>
          Also set: {unknown.map((b) => b.name || b.code).join(", ")} — not in this area's machine groups.
        </Text>
      ) : null}
    </>
  );
}

/** One side of a movement (Source / Destination Equipment), as /process-config's cascader saves it:
 *  one machine group, and machines of it — the whole group or some. `{group_id, machine_ids}`. */
export function MachineSidePicker({ value, readOnly, onChange, placeholder }) {
  const tree = useMachineTree();
  const groupOf = useMemo(() => {
    const out = {};
    (tree || []).forEach((c) => c.children.forEach((g) => g.children.forEach((m) => { out[m.value] = [c.value, g.value]; })));
    return out;
  }, [tree]);
  const ids = value?.machine_ids || [];
  const paths = ids.map((id) => (groupOf[id] ? [...groupOf[id], id] : null)).filter(Boolean);
  return (
    <Cascader size="small" multiple showCheckedStrategy={Cascader.SHOW_CHILD}
      style={{ width: "100%" }} disabled={readOnly || !tree} loading={!tree} expandTrigger="hover"
      options={tree || []} value={paths} placeholder={placeholder || "Machine category → group → machines"}
      showSearch={{ filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }}
      onChange={(next) => {
        const leaves = (next || []).filter((p) => typeof p[p.length - 1] === "number");
        if (!leaves.length) { onChange(null); return; }
        // One group per side: the group of the latest pick wins, as the dialog allows one group.
        const last = leaves[leaves.length - 1];
        const group = last[1];
        const kept = leaves.filter((p) => p[1] === group).map((p) => p[p.length - 1]);
        onChange({ category_id: Number(String(last[0]).slice(1)), group_id: Number(String(group).slice(1)), machine_ids: kept });
      }} />
  );
}

